import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import BarChartIcon from '@mui/icons-material/BarChart';
import BlockIcon from '@mui/icons-material/Block';
import CableIcon from '@mui/icons-material/Cable';
import CampaignIcon from '@mui/icons-material/Campaign';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import ContactsIcon from '@mui/icons-material/Contacts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FeedbackIcon from '@mui/icons-material/Feedback';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import InsightsIcon from '@mui/icons-material/Insights';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LinkIcon from '@mui/icons-material/Link';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QueueIcon from '@mui/icons-material/Queue';
import SearchIcon from '@mui/icons-material/Search';
import SellIcon from '@mui/icons-material/Sell';
import AiChatIcon from './ai-chat-icon';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { FunnelTypes } from 'features/co-pilot/co-pilot.types';
import useOpenTaskCount from 'features/co-pilot/task-manager/use-open-task-count';
import { useLinkedinAccountsQuery } from 'features/co-pilot/linkedin-accounts/hooks/use-linkedin-accounts-query';
import TerminalIcon from '@mui/icons-material/Terminal';
import TimelineIcon from '@mui/icons-material/Timeline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import { ProductGroups, Products, Roles, Routes } from 'enums';
import { getAccountTabRoute, useAccountManagementTabs } from 'features/account/account-management/account-management-tabs';
import { getCoPilotTabRoutes } from 'features/co-pilot/co-pilot-routes';
import { useLicenseStatus } from 'hooks/use-license-status';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationStates } from 'stores/app-bar-navigation-store';
import { allowContactManagement } from 'utils/blacklist.utils';

export const NAV_RAIL_WIDTH = 76;
export const CONTEXT_PANEL_WIDTH = 248;
export const TOP_BAR_HEIGHT = 60;

export type NavSectionKey =
  | 'chat'
  | 'recruiting'
  | 'sales'
  | 'tests'
  | 'contacts'
  | 'research'
  | 'store'
  | 'apps'
  | 'admin'
  | 'account';

export interface PanelItem {
  text: string;
  route: string;
  icon: React.ReactNode;
  /** Match active state with pathname === route instead of startsWith. */
  matchExact?: boolean;
  /** Renders the item under the "Einstellungen" group divider. */
  settings?: boolean;
  /** Zähler am Eintrag (z. B. offene Aufgaben); 0/undefined = kein Zähler. */
  badge?: number;
}

export interface NavSection {
  key: NavSectionKey;
  label: string;
  icon: React.ReactNode;
  /** Route the rail item navigates to (first panel entry). */
  route: string;
  /** A pathname belongs to this section when it starts with one of these prefixes. */
  pathPrefixes: string[];
  panelItems: PanelItem[];
  /** Rail item at the bottom group (Apps/Admin) instead of the top group. */
  bottom?: boolean;
  /** Zähler am Rail-Symbol (offene Aufgaben im Bereich). */
  badge?: number;
  disabled?: boolean;
  /** Only a context panel, no rail item (Account is reached via the account menu at the rail bottom). */
  hiddenInRail?: boolean;
}

/**
 * Single source of truth for the rail + context-panel navigation
 * (design "Navigation 1b": rail lists every area, the panel only
 * shows the active area's sub-navigation).
 */
export const useNavSections = (): NavSection[] => {
  const { t } = useTranslation();
  const { role, isPlatformAdmin } = useUserData();

  const licenseRecruiter = useLicenseStatus(Products.SAIA_RECRUITING);
  const licenseLeadGen = useLicenseStatus(Products.SAIA_SALES);
  const licenseMotive = useLicenseStatus(Products.MOTIVE);
  const licenseLeadTest = useLicenseStatus(Products.LEAD);

  const accountTabs = useAccountManagementTabs();
  const recruitingTaskCount = useOpenTaskCount(licenseRecruiter === 'valid', FunnelTypes.ACTIVE_SOURCING_CO_PILOT);
  const salesTaskCount = useOpenTaskCount(licenseLeadGen === 'valid', FunnelTypes.SOCIAL_SELLING_CO_PILOT);
  // Kontakte: jeder mit mindestens einem LinkedIn-Account, dazu wie bisher Admins/Owner und die Whitelist.
  const { data: linkedinAccounts } = useLinkedinAccountsQuery();
  const showContacts = (linkedinAccounts?.length ?? 0) > 0 || allowContactManagement(role);

  const recruitingRoutes = getCoPilotTabRoutes(ProductGroups.HR_BOOSTER);
  const salesRoutes = getCoPilotTabRoutes(ProductGroups.LEAD_BOOSTER);

  const coPilotPanelItems = (routes: ReturnType<typeof getCoPilotTabRoutes>, openTaskCount: number): PanelItem[] => [
    { text: t('campaignOverview'), route: routes[NavigationStates.PROJECTS], icon: <ViewListIcon /> },
    { text: t(NavigationStates.INBOX), route: routes[NavigationStates.INBOX], icon: <MailOutlineIcon /> },
    { text: t(NavigationStates.TASK_MANAGER), route: routes[NavigationStates.TASK_MANAGER], icon: <TaskAltIcon />, badge: openTaskCount },
    { text: t(NavigationStates.STATISTICS), route: routes[NavigationStates.STATISTICS], icon: <BarChartIcon /> },
    {
      text: t(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT),
      route: routes[NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT],
      icon: <LinkIcon />,
      settings: true,
    },
    { text: t(NavigationStates.BLACKLIST), route: routes[NavigationStates.BLACKLIST], icon: <BlockIcon />, settings: true },
  ];

  // Recruiting/Sales nur zeigen, wenn der Nutzer für das Produkt eine Lizenz hat (ein Bundle
  // bringt beide Lizenzen mit). Abgelaufene Lizenz: Bereich bleibt sichtbar, aber ausgegraut.
  const sections: (NavSection | null)[] = [
    {
      key: 'chat',
      label: t('navRailChat', 'Chat'),
      icon: <AiChatIcon />,
      route: Routes.AI_CHAT,
      pathPrefixes: [Routes.AI_CHAT],
      // The chat page brings its own sidebar (chat-sidebar.tsx) — no generic panel.
      panelItems: [],
    },
    licenseRecruiter === 'none'
      ? null
      : {
          key: 'recruiting',
          label: t('navRailRecruiting', 'Recruiting'),
          icon: <BadgeIcon />,
          route: recruitingRoutes[NavigationStates.PROJECTS],
          pathPrefixes: [Routes.RECRUITING_BOOSTER_CO_PILOT, Routes.HR_BOOSTER],
          // „Kampagne bearbeiten" gibt es nicht mehr als eigenen Menüpunkt — der Editor öffnet sich
          // aus der Kampagnen-Übersicht.
          panelItems: coPilotPanelItems(recruitingRoutes, recruitingTaskCount),
          disabled: licenseRecruiter !== 'valid',
          badge: licenseRecruiter === 'valid' ? recruitingTaskCount : undefined,
        },
    licenseLeadGen === 'none'
      ? null
      : {
          key: 'sales',
          label: t('navRailSales', 'Sales'),
          icon: <TrendingUpIcon />,
          route: salesRoutes[NavigationStates.PROJECTS],
          pathPrefixes: [Routes.LEAD_BOOSTER_CO_PILOT, Routes.LEAD_BOOSTER, Routes.PIPELINE],
          panelItems: [
            ...coPilotPanelItems(salesRoutes, salesTaskCount),
            { text: t('pipeline', 'Pipeline'), route: Routes.PIPELINE, icon: <ViewKanbanIcon /> },
          ],
          disabled: licenseLeadGen !== 'valid',
          badge: licenseLeadGen === 'valid' ? salesTaskCount : undefined,
        },
    licenseMotive === 'valid' || licenseLeadTest === 'valid'
      ? {
          key: 'tests',
          label: t('navRailTests', 'Tests'),
          icon: <PsychologyIcon />,
          route: Routes.SURVEY_OVERVIEW,
          pathPrefixes: [Routes.SURVEY_OVERVIEW, '/products/lead'],
          panelItems: [
            { text: t('motiveAnalysis'), route: Routes.SURVEY_OVERVIEW, icon: <InsightsIcon /> },
            { text: t('leadSection'), route: Routes.LEAD_CREATION, icon: <FactCheckIcon /> },
          ],
        }
      : null,
    showContacts
      ? {
          key: 'contacts',
          label: t('navRailContacts', 'Kontakte'),
          icon: <ContactsIcon />,
          route: Routes.CONTACT_MANAGEMENT_OVERVIEW,
          pathPrefixes: [Routes.CONTACT_MANAGEMENT],
          panelItems: [
            { text: t('contactOverview'), route: Routes.CONTACT_MANAGEMENT_OVERVIEW, icon: <PeopleOutlineIcon /> },
            { text: t(NavigationStates.TAG_MANAGEMENT), route: Routes.CONTACT_MANAGEMENT_TAG_MANAGEMENT, icon: <SellIcon /> },
            { text: t('integrations'), route: Routes.CONTACT_MANAGEMENT_INTEGRATIONS, icon: <CableIcon /> },
          ],
        }
      : null,
    // Research und Vorlagen nur für Plattform-Admins — normale Kunden sehen sie nicht
    isPlatformAdmin && licenseRecruiter === 'valid'
      ? {
          key: 'research',
          label: t('navRailResearch', 'Research'),
          icon: <TravelExploreIcon />,
          route: Routes.LEAD_LIST_RESEARCH,
          pathPrefixes: [Routes.LEAD_LIST_RESEARCH, Routes.LEAD_LIST_STORAGE],
          panelItems: [
            { text: t('leadListOverview'), route: Routes.LEAD_LIST_RESEARCH, icon: <SearchIcon /> },
            { text: t('leadListResults'), route: Routes.LEAD_LIST_STORAGE, icon: <Inventory2Icon /> },
          ],
        }
      : null,
    isPlatformAdmin
      ? {
          key: 'store',
          label: t('navRailStore', 'Vorlagen'),
          icon: <CollectionsBookmarkIcon />,
          route: Routes.USE_CASE_STORE,
          pathPrefixes: [Routes.USE_CASE_STORE],
          panelItems: [],
        }
      : null,
    role === Roles.ADMIN || role === Roles.MANAGER || role === Roles.AGENCY || role === Roles.OWNER
      ? {
          key: 'apps',
          label: t('navRailApps', 'Apps'),
          icon: <IntegrationInstructionsIcon />,
          route: Routes.WEBMETICS_OVERVIEW,
          pathPrefixes: [Routes.WEBMETICS_OVERVIEW],
          bottom: true,
          panelItems: [{ text: t('webmeticsOverview', 'Webmetics-Übersicht'), route: Routes.WEBMETICS_OVERVIEW, icon: <DashboardIcon /> }],
        }
      : null,
    role === Roles.OWNER
      ? {
          key: 'admin',
          label: t('navRailAdmin', 'Admin'),
          icon: <AdminPanelSettingsIcon />,
          route: Routes.ADMINISTRATION,
          pathPrefixes: [Routes.ADMINISTRATION],
          bottom: true,
          panelItems: [
            { text: t('adminSectionOverview'), route: Routes.ADMINISTRATION, icon: <DashboardIcon />, matchExact: true },
            { text: t('prompts'), route: Routes.PROMPT_MANAGEMENT, icon: <TerminalIcon /> },
            { text: t('statusMessages'), route: Routes.STATUS_MESSAGE_MANAGEMENT, icon: <CampaignIcon /> },
            { text: t('adminNavMonitoring'), route: Routes.ADMIN_MONITORING, icon: <MonitorHeartIcon /> },
            { text: t('adminNavUserOverview'), route: Routes.ADMIN_USER_OVERVIEW, icon: <GroupIcon /> },
            { text: t('adminNavQueue'), route: Routes.ADMIN_QUEUE_MANAGEMENT, icon: <QueueIcon /> },
            { text: t('adminNavLinkedinSession24h'), route: Routes.ADMIN_LINKEDIN_SESSION_MONITOR, icon: <HistoryIcon /> },
            { text: t('adminNavCampaignTimeline'), route: Routes.ADMIN_CAMPAIGN_CONTACT_TIMELINE, icon: <TimelineIcon /> },
            { text: t('adminNavFeedbackTickets'), route: Routes.ADMIN_FEEDBACK_TICKETS, icon: <FeedbackIcon /> },
            { text: t('adminNavEngagement'), route: Routes.ADMIN_ENGAGEMENT, icon: <InsightsIcon /> },
          ],
        }
      : null,
    {
      key: 'account',
      label: t('account'),
      icon: <ManageAccountsIcon />,
      route: getAccountTabRoute(accountTabs[0].value),
      pathPrefixes: [Routes.ACCOUNT],
      hiddenInRail: true,
      panelItems: accountTabs.map((tab) => ({ text: tab.label, route: getAccountTabRoute(tab.value), icon: tab.icon })),
    },
  ];

  return sections.filter((s): s is NavSection => s !== null);
};

/** Longest-prefix match so e.g. /lead-booster/co-pilot wins over /lead-booster. */
export const getActiveSection = (sections: NavSection[], pathname: string): NavSection | undefined => {
  let best: NavSection | undefined;
  let bestLength = -1;
  for (const section of sections) {
    for (const prefix of section.pathPrefixes) {
      if (pathname.startsWith(prefix) && prefix.length > bestLength) {
        best = section;
        bestLength = prefix.length;
      }
    }
  }
  return best;
};

export const getActivePanelItem = (section: NavSection | undefined, pathname: string): PanelItem | undefined => {
  if (!section) return undefined;
  // Der Wizard „Neue Kampagne“ gehört zur Kampagnen-Übersicht (erster Eintrag des Recruiting-Bereichs).
  if (section.key === 'recruiting' && pathname.startsWith(Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN)) return section.panelItems[0];
  let best: PanelItem | undefined;
  let bestLength = -1;
  for (const item of section.panelItems) {
    if (item.matchExact) {
      if (pathname === item.route && item.route.length > bestLength) {
        best = item;
        bestLength = item.route.length;
      }
    } else if (pathname.startsWith(item.route) && item.route.length > bestLength) {
      best = item;
      bestLength = item.route.length;
    }
  }
  return best;
};
